import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, PlayCircle } from "lucide-react";
import { fetchProfile, fetchProjects } from "@/lib/api";
import { usePageMeta } from "@/components/Seo";

const ROLE_LABEL = {
  director: "Direction",
  producer: "Production",
  writer: "Writing",
  photographer: "Photography",
};

export default function Home() {
  const [profile, setProfile] = useState(null);
  const [projects, setProjects] = useState([]);

  usePageMeta({
    description:
      "Chinmaya Sharma — filmmaker, founder of Qissa Studios. 100+ commercials for Netflix, Google, RedBull and more, award-travelling short films, and the poetry collection Gubaar.",
  });

  useEffect(() => {
    fetchProfile().then(setProfile).catch(() => {});
    fetchProjects().then(setProjects).catch(() => {});
  }, []);

  const featured = projects.filter((p) => p.featured);
  const isCommercial = (p) => p.sub_category === "commercials" || p.sub_category === "qissa";
  const featuredCommercials = featured.filter(isCommercial);
  const featuredOther = featured.filter((p) => !isCommercial(p));
  const featuredOrdered = [...featuredCommercials, ...featuredOther].slice(0, 6);
  const rest = projects.filter((p) => !p.featured).slice(0, 4);

  return (
    <main data-testid="home-page" className="bg-zinc-950 text-zinc-50">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_media-portfolio-95/artifacts/fm5n2gjt_hero%20index.jpg"
            alt="Chinmaya Sharma on set"
            className="w-full h-full object-cover slow-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/40 via-zinc-950/30 to-zinc-950" />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/60 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto w-full px-6 md:px-12 lg:px-16 pb-20 md:pb-28">
          <p
            data-testid="hero-overline"
            className="text-[11px] uppercase tracking-[0.4em] text-zinc-400 mb-8 reveal"
            style={{ animationDelay: "0.1s" }}
          >
            Chinmaya Sharma · Writer · Director · Producer · Qissa Studios
          </p>
          <h1
            data-testid="hero-headline"
            className="font-serif text-5xl sm:text-7xl md:text-8xl lg:text-[9rem] leading-[0.95] tracking-tight reveal"
            style={{ animationDelay: "0.25s" }}
          >
            The set, the sweat,
            <br />
            <span className="italic font-light text-zinc-300">
              and the storytelling.
            </span>
          </h1>

          <div
            className="mt-12 max-w-2xl reveal"
            style={{ animationDelay: "0.45s" }}
          >
            <p className="text-zinc-300 text-lg leading-relaxed">
              {profile?.bio?.split(".").slice(0, 2).join(".") + "."}
            </p>
          </div>

          <div
            className="mt-12 flex flex-wrap items-center gap-6 reveal"
            style={{ animationDelay: "0.6s" }}
          >
            <Link
              to="/director"
              data-testid="hero-cta-disciplines"
              className="group inline-flex items-center gap-3 bg-white text-zinc-950 px-8 py-4 text-[11px] uppercase tracking-[0.3em] hover:bg-zinc-200 transition-colors"
            >
              Enter the disciplines
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
            </Link>
            <Link
              to="/about"
              data-testid="hero-cta-about"
              className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-zinc-300 hover:text-white"
            >
              About the studio
              <ArrowUpRight className="h-3 w-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.5} />
            </Link>
          </div>
        </div>

        <div className="absolute right-6 md:right-12 lg:right-16 bottom-20 md:bottom-28 hidden md:block z-10">
          <div className="w-px h-24 bg-zinc-500 mx-auto mb-3" />
          <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 rotate-90 origin-center mt-12">
            Scroll
          </p>
        </div>
      </section>

      {/* ROLES MARQUEE */}
      <section className="border-y border-zinc-900 overflow-hidden py-10 bg-zinc-950">
        <div className="marquee-track flex gap-16 whitespace-nowrap">
          {[...Array(2)].map((_, dup) => (
            <div key={dup} className="flex gap-16 items-center">
              {(profile?.roles || ["Producer", "Writer", "Director", "Photographer"]).map((r) => (
                <span
                  key={`${dup}-${r}`}
                  className="font-serif italic text-3xl md:text-5xl text-zinc-200"
                >
                  {r}
                  <span className="text-zinc-700 mx-12">·</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* SHOWREEL STRIP */}
      {featuredOrdered.length > 0 && (
        <section data-testid="showreel-section" className="py-20 md:py-28 overflow-hidden">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 mb-10 flex items-baseline justify-between">
            <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500">
              Showreel · Best frames
            </p>
            <p className="hidden md:block text-[10px] uppercase tracking-[0.25em] text-zinc-600">
              Hover to pause · Click to watch
            </p>
          </div>
          <div className="showreel-strip relative border-y border-zinc-900 bg-zinc-950 py-4">
            <div className="showreel-track flex gap-4 w-max">
              {[...Array(2)].map((_, dup) => (
                <div key={dup} className="flex gap-4">
                  {featuredOrdered.map((p) => (
                    <Link
                      key={`${dup}-${p.slug}`}
                      to={`/work/${p.slug}`}
                      data-testid={dup === 0 ? `showreel-frame-${p.slug}` : undefined}
                      className="group relative block w-[300px] md:w-[420px] aspect-video shrink-0 overflow-hidden bg-zinc-900"
                    >
                      <img
                        src={p.cover}
                        alt={p.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="absolute bottom-0 left-0 right-0 p-4 flex items-end justify-between opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                        <span className="font-serif text-lg text-zinc-100 leading-tight pr-3">
                          {p.title}
                        </span>
                        <PlayCircle className="h-5 w-5 text-zinc-100 shrink-0" strokeWidth={1} />
                      </div>
                    </Link>
                  ))}
                </div>
              ))}
            </div>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-20 md:w-40 bg-gradient-to-r from-zinc-950 to-transparent z-10" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-20 md:w-40 bg-gradient-to-l from-zinc-950 to-transparent z-10" />
          </div>
        </section>
      )}

      {/* FEATURED WORK — uniform grid */}
      <section className="py-24 md:py-40 px-6 md:px-12 lg:px-16 max-w-[1600px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-6">
              Selected Work / 2020 — 2025
            </p>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-tight max-w-3xl">
              Stories that have been{" "}
              <span className="italic text-zinc-400">waiting</span> to be told.
            </h2>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 self-start md:self-auto">
            {[
              ["/director", "Director"],
              ["/production", "Production"],
              ["/writing", "Writing"],
              ["/photography", "Photography"],
            ].map(([to, label]) => (
              <Link
                key={to}
                to={to}
                data-testid={`featured-discipline-${label.toLowerCase()}`}
                className="group inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.3em] text-zinc-400 hover:text-white border-b border-zinc-700 hover:border-white pb-1 transition-colors"
              >
                {label}
                <ArrowUpRight className="h-3 w-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.5} />
              </Link>
            ))}
          </div>
        </div>

        {featuredOrdered.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {featuredOrdered.map((p) => (
              <FeaturedCard key={p.slug} project={p} />
            ))}
          </div>
        )}

        {/* Secondary list */}
        <div className="mt-32 border-t border-zinc-900">
          {rest.map((p, i) => (
            <Link
              key={p.slug}
              to={`/work/${p.slug}`}
              data-testid={`home-list-project-${p.slug}`}
              className="group grid grid-cols-12 gap-6 items-center py-8 border-b border-zinc-900 hover:bg-zinc-900/30 transition-colors px-2"
            >
              <span className="col-span-1 text-zinc-600 text-sm tabular-nums">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="col-span-6 md:col-span-5 font-serif text-2xl md:text-3xl text-zinc-100 group-hover:italic transition-all">
                {p.title}
              </span>
              <span className="hidden md:block col-span-3 text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                {ROLE_LABEL[p.role]}
              </span>
              <span className="col-span-3 md:col-span-2 text-sm text-zinc-500 tabular-nums">
                {p.year}
              </span>
              <span className="col-span-2 md:col-span-1 justify-self-end text-zinc-500 group-hover:text-white transition-colors">
                <ArrowUpRight className="h-5 w-5" strokeWidth={1.5} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* DISCIPLINES — four entry points */}
      <section className="border-t border-zinc-900 px-6 md:px-12 lg:px-16 py-24 md:py-32 max-w-[1600px] mx-auto">
        <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-10">
          The four disciplines
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2 md:gap-y-4">
          {[
            { to: "/director", label: "Director", italic: "confident in every frame", num: "01" },
            { to: "/production", label: "Production", italic: "Qissa Studios — 100+ films", num: "02" },
            { to: "/writing", label: "Writing", italic: "ग़ुबार · Gubaar", num: "03" },
            { to: "/photography", label: "Photography", italic: "where it all began", num: "04" },
          ].map((d) => (
            <Link
              key={d.to}
              to={d.to}
              data-testid={`home-discipline-${d.label.toLowerCase()}`}
              className="group flex items-baseline justify-between border-b border-zinc-900 py-8 hover:border-zinc-700 transition-colors"
            >
              <div className="flex items-baseline gap-6">
                <span className="text-zinc-600 text-xs tabular-nums">{d.num}</span>
                <h3 className="font-serif text-4xl md:text-6xl text-zinc-100 group-hover:text-white transition-colors">
                  {d.label}
                </h3>
              </div>
              <div className="hidden md:flex items-baseline gap-4">
                <span className="font-serif italic text-zinc-500 group-hover:text-zinc-300 transition-colors">
                  {d.italic}
                </span>
                <ArrowUpRight className="h-5 w-5 text-zinc-500 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" strokeWidth={1.5} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* RECOGNITION — laurels & press */}
      {profile?.awards?.length > 0 && (
        <section data-testid="awards-strip" className="border-t border-zinc-900 px-6 md:px-12 lg:px-16 py-24 md:py-32 max-w-[1600px] mx-auto">
          <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-14">
            Recognition · Laurels & press
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900">
            {profile.awards.map((a, i) => (
              <div
                key={i}
                data-testid={`award-item-${i}`}
                className="bg-zinc-950 p-8 md:p-10 flex flex-col justify-between min-h-[220px] hover:bg-zinc-900/50 transition-colors"
              >
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-5">
                    {a.org}
                  </p>
                  <h3 className="font-serif text-2xl md:text-[1.7rem] text-zinc-100 leading-tight">
                    {a.title}
                  </h3>
                </div>
                <div className="mt-8 flex items-baseline justify-between gap-4">
                  <p className="font-serif italic text-zinc-500 text-sm leading-snug">
                    {a.for}
                  </p>
                  <span className="text-zinc-600 text-xs tabular-nums shrink-0">{a.year}</span>
                </div>
              </div>
            ))}
          </div>
          {profile?.press?.length > 0 && (
            <div className="mt-14 flex flex-wrap gap-x-10 gap-y-4">
              {profile.press.map((p, i) => (
                <a
                  key={i}
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`press-link-${i}`}
                  className="group inline-flex items-baseline gap-2 text-zinc-400 hover:text-white transition-colors"
                >
                  <span className="text-[11px] uppercase tracking-[0.25em]">{p.outlet}</span>
                  <span className="font-serif italic text-sm text-zinc-500 group-hover:text-zinc-300 transition-colors">
                    {p.title}
                  </span>
                  <ArrowUpRight className="h-3 w-3 text-zinc-600 group-hover:text-white transition-colors" strokeWidth={1.5} />
                </a>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Quote / philosophy */}
      <section className="py-24 md:py-40 px-6 md:px-12 lg:px-16 max-w-[1400px] mx-auto">
        <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-12">
          On practice
        </p>
        <blockquote className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.15] text-zinc-100">
          "Every frame is a question.{" "}
          <span className="italic text-zinc-400">
            The cut is the answer.
          </span>
          "
        </blockquote>
        <footer className="mt-10 text-[11px] uppercase tracking-[0.3em] text-zinc-500">
          — Chinmaya Sharma · From a director's note
        </footer>
      </section>
    </main>
  );
}

function FeaturedCard({ project }) {
  if (!project) return null;
  return (
    <Link
      to={`/work/${project.slug}`}
      data-testid={`featured-project-${project.slug}`}
      className="group block"
    >
      <div className="relative aspect-video overflow-hidden bg-zinc-900">
        <img
          src={project.cover}
          alt={project.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
        />
        {project.video_embed && (
          <div className="absolute top-4 right-4 z-10 text-zinc-100 opacity-80 group-hover:opacity-100 transition-opacity">
            <PlayCircle className="h-6 w-6" strokeWidth={1} />
          </div>
        )}
      </div>
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">
            {ROLE_LABEL[project.role]}
          </p>
          <p className="text-[10px] text-zinc-500 tabular-nums">{project.year}</p>
        </div>
        <h3 className="font-serif text-2xl md:text-[1.65rem] text-zinc-100 group-hover:text-white transition-colors leading-tight">
          {project.title}
        </h3>
      </div>
    </Link>
  );
}
